import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, TrendingDown, Users, MessageSquare, MousePointerClick, Target } from 'lucide-react';
import { mockAnalytics } from '../data/mockData';

const Analytics = () => {
  const metrics = [
    {
      title: 'Total Subscribers',
      value: mockAnalytics.totalSubscribers.toLocaleString(),
      change: '+12.5%',
      trend: 'up',
      icon: Users
    },
    {
      title: 'Messages Sent',
      value: mockAnalytics.messagesSent.toLocaleString(),
      change: '+8.2%',
      trend: 'up',
      icon: MessageSquare
    },
    {
      title: 'Open Rate',
      value: `${mockAnalytics.openRate}%`,
      change: '+3.1%',
      trend: 'up',
      icon: MousePointerClick
    },
    {
      title: 'Conversion Rate',
      value: `${mockAnalytics.conversionRate}%`,
      change: '+2.4%',
      trend: 'up',
      icon: Target
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1">Track your performance and engagement metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            const TrendIcon = metric.trend === 'up' ? TrendingUp : TrendingDown;
            return (
              <Card key={metric.title}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">{metric.title}</p>
                    <Icon className="h-5 w-5 text-gray-400" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{metric.value}</p>
                  <div className={`flex items-center text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <TrendIcon className="h-4 w-4 mr-1" />
                    {metric.change} from last week
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Growth Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Subscriber Growth</CardTitle>
              <CardDescription>Last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end justify-between space-x-2">
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '70%' }} title="3200 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Mon</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '75%' }} title="3450 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Tue</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '80%' }} title="3680 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Wed</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '86%' }} title="3920 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Thu</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '91%' }} title="4150 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Fri</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '94%' }} title="4320 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Sat</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: '100%' }} title="4580 subscribers" />
                  <span className="text-xs text-gray-500 mt-2">Sun</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Messages Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Messages Sent</CardTitle>
              <CardDescription>Last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end justify-between space-x-2">
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '64%' }} title="12400 messages" />
                  <span className="text-xs text-gray-500 mt-2">Mon</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '68%' }} title="13200 messages" />
                  <span className="text-xs text-gray-500 mt-2">Tue</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '72%' }} title="14100 messages" />
                  <span className="text-xs text-gray-500 mt-2">Wed</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '79%' }} title="15300 messages" />
                  <span className="text-xs text-gray-500 mt-2">Thu</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '83%' }} title="16200 messages" />
                  <span className="text-xs text-gray-500 mt-2">Fri</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '91%' }} title="17800 messages" />
                  <span className="text-xs text-gray-500 mt-2">Sat</span>
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer" style={{ height: '100%' }} title="19500 messages" />
                  <span className="text-xs text-gray-500 mt-2">Sun</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Distribution</CardTitle>
            <CardDescription>Subscriber distribution across platforms</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">Messenger</span>
                  <span className="text-sm text-gray-600">2,340 (51%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-blue-500 h-3 rounded-full transition-all" style={{ width: '51%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">Instagram</span>
                  <span className="text-sm text-gray-600">1,450 (32%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-pink-500 h-3 rounded-full transition-all" style={{ width: '32%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">WhatsApp</span>
                  <span className="text-sm text-gray-600">790 (17%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: '17%' }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
